import { AiBadge } from "@/components/ax/AiBlock";
import { Button } from "@/components/ax/Button";
import { useI18n } from "@/i18n";
import { ChartNoAxesCombined } from "lucide-react";
import styles from "@/components/sheet/LembarVisual.module.css";

const Part = ({ title, children }) => (
  <div className={styles.readingPart}>
    <h3 className="m-0 text-sm font-semibold leading-5 text-ax-text">{title}</h3>
    <div className="mt-2">{children}</div>
  </div>
);

const Lines = ({ signals, testId }) => (
  <ul data-testid={testId} className="m-0 flex list-none flex-col gap-2 p-0">
    {signals.map((s) => (
      <li key={s.text} className={styles.signal}>
        <span>{s.text}</span>
      </li>
    ))}
  </ul>
);

export const ReadingSkeleton = () => {
  const { t } = useI18n();
  return (
    <section aria-busy="true" data-testid="sheet-reading-skeleton" className={styles.reading}>
      <AiBadge />
      <div className="mt-3 flex flex-col gap-3">
        {["100%", "92%", "70%"].map((w) => (
          <div key={w} style={{ width: w }} className="h-2 rounded-[8px] bg-ax-border" />
        ))}
      </div>
      <p className="m-0 mt-3 text-sm leading-5 text-ax-text2">{t("sheet.reading")}</p>
    </section>
  );
};

export const Reading = ({ block, index, onRaise, canRaise }) => {
  const { t } = useI18n();
  const seen = block.signals.filter((s) => s.kind === "pattern");
  const unclear = block.signals.filter((s) => s.kind !== "pattern");

  return (
    <section data-testid={`sheet-reading-${index}`} className={styles.reading}>
      <div className={styles.readingHeader}><AiBadge /><span>{t("sheet.synthesis")}</span><ChartNoAxesCombined size={20} strokeWidth={1.5} aria-hidden="true" /></div>
      {block.abstained ? (
        <p data-testid="sheet-abstain" className="m-0 mt-3 text-base leading-6 text-ax-text">
          {t("sheet.abstain")}
        </p>
      ) : (
        <>
          <Part title={t("sheet.sumHead")}>
            <p data-testid={`sheet-summary-${index}`} className="m-0 text-base leading-6 text-ax-text [overflow-wrap:anywhere]">
              {block.summary}
            </p>
          </Part>
          {seen.length > 0 && (
            <Part title={t("sheet.seenHead")}>
              <Lines signals={seen} testId={`sheet-seen-${index}`} />
            </Part>
          )}
          {unclear.length > 0 && (
            <Part title={t("sheet.unclearHead")}>
              <Lines signals={unclear} testId={`sheet-unclear-${index}`} />
            </Part>
          )}
          {block.questions.length > 0 && (
            <Part title={t("sheet.raiseHead")}>
              <ul data-testid={`sheet-questions-${index}`} className="m-0 flex list-none flex-col gap-2 p-0">
                {block.questions.map((q) => (
                  <li key={q.id} className="flex min-h-[44px] flex-wrap items-center justify-between gap-x-4 gap-y-1">
                    <span className="min-w-0 flex-1 basis-full text-base leading-6 text-ax-text [overflow-wrap:anywhere] sm:basis-auto">
                      {q.text}
                    </span>
                    {canRaise && (
                      <Button
                        variant="ghost"
                        onClick={() => onRaise(q)}
                        data-testid={`sheet-raise-${index}-${q.id}`}
                        className="shrink-0 px-0"
                      >
                        {t("sheet.promote")}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </Part>
          )}
        </>
      )}
    </section>
  );
};
